import { useEffect, useState, useRef } from "react";
import { Upload, X, Loader2, PenLine, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ACCEPT = ".jpg,.jpeg,.png";
const MAX_KB = 500;

/**
 * R30 Phase B — My Signature panel for Settings.
 * Self-service signature upload/delete. Image is embedded in approved-export
 * Excel/PDF quotes (NPD + Assembly + Project children) ABOVE "Authorised Signatory".
 */
export default function MySignaturePanel() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileRef = useRef(null);

  const hasSig = !!user?.signature_image_path;

  useEffect(() => {
    let revoke;
    if (hasSig && user?.user_id) {
      // R30 polish — Authorization header from api interceptor is sufficient;
      // no need to leak the token via query string.
      api
        .get(`/users/${user.user_id}/signature`, { responseType: "blob" })
        .then((r) => {
          const url = URL.createObjectURL(r.data);
          revoke = url;
          setPreviewUrl(url);
        })
        .catch(() => setPreviewUrl(null));
    } else {
      setPreviewUrl(null);
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [hasSig, user?.user_id, user?.signature_uploaded_at]);

  const onUpload = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_KB * 1024) {
      toast.error(`Signature too large (max ${MAX_KB} KB). Got ${(file.size / 1024).toFixed(0)} KB`);
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/me/signature", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Signature uploaded — will appear on all your future approved exports");
      // Force-refresh auth so the previewUrl effect re-fires.
      if (refresh) await refresh();
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error("Object storage credits exhausted — please ask the admin to top up.");
      } else {
        toast.error(e?.response?.data?.detail || "Upload failed");
      }
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Remove your signature? Future exports will not include it until you upload again.")) return;
    try {
      await api.delete("/me/signature");
      toast.success("Signature removed");
      if (refresh) await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6" data-testid="my-signature-panel">
      <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
        <PenLine size={16} className="text-orange-600" /> My Signature
      </h3>
      <p className="text-xs text-slate-500 mb-4">
        Upload a JPG/PNG of your handwritten signature (max {MAX_KB} KB).
        It will be embedded above <em>Authorised Signatory</em> on every Excel
        quote you approve. Every embed is logged in a tamper-evident audit trail.
      </p>
      {hasSig ? (
        <div className="flex items-start gap-4 p-3 border border-slate-200 rounded-md bg-slate-50">
          <div className="w-40 h-20 bg-white border border-slate-200 rounded-md overflow-hidden flex-shrink-0 flex items-center justify-center">
            {previewUrl ? (
              <img src={previewUrl} alt="My signature" className="w-full h-full object-contain" />
            ) : (
              <Loader2 size={20} className="text-slate-300 animate-spin" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900">Signature on file</div>
            <div className="text-xs text-slate-500">
              {user?.signature_size_bytes ? `${(user.signature_size_bytes / 1024).toFixed(1)} KB` : ""}
              {user?.signature_uploaded_at && (
                <> · uploaded {new Date(user.signature_uploaded_at).toLocaleDateString("en-IN")}</>
              )}
            </div>
            <div className="mt-2 flex gap-2">
              <label className="text-xs text-slate-600 hover:text-slate-900 cursor-pointer flex items-center gap-1" data-testid="signature-replace">
                <Upload size={12} /> Replace
                <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={onUpload} disabled={busy} />
              </label>
              <button type="button" onClick={onDelete} className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1" data-testid="signature-delete">
                <X size={12} /> Remove
              </button>
            </div>
          </div>
        </div>
      ) : (
        <label
          className="border-2 border-dashed border-orange-300 hover:border-orange-500 rounded-md p-6 flex flex-col items-center justify-center text-center cursor-pointer transition-colors bg-orange-50/30 hover:bg-orange-50"
          data-testid="signature-upload-zone"
        >
          {busy ? (
            <><Loader2 size={28} className="text-orange-500 animate-spin mb-2" /><span className="text-sm text-slate-600">Uploading…</span></>
          ) : (
            <>
              <Upload size={28} className="text-orange-500 mb-2" />
              <span className="text-sm font-medium text-slate-700">Click to upload your signature</span>
              <span className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                <AlertCircle size={11} /> JPG or PNG · max {MAX_KB} KB
              </span>
            </>
          )}
          <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={onUpload} disabled={busy} />
        </label>
      )}
    </div>
  );
}
